import get from 'lodash/get'
import React, { SyntheticEvent } from 'react'
import { BCol, BForm, BLink, BRow, BText } from '@prague-digi/ui-components'
import { compose } from 'recompose'
import { connect, ConnectedProps } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import { change } from 'redux-form'

import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import validate from './ClientInfo.validate'
import {
  analyticsActions,
  clientActions,
  modalDialogActions,
  notificationActions,
} from '../../redux'
import {
  API_ERROR,
  CLIENT_INFO_ENCRYPTION,
  CLIENT_INFO_MARKETING,
  CLIENT_INFO_IM,
} from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'
import {
  BCheckbox,
  BPhoneInput,
  BTextInput,
  PageControlButtons,
  PrefillFormButton,
  Title,
} from '../../components'
import { ReduxState } from '../../types'
import { NotificationSeverityEnum } from '../../redux/reducers/notification/model'
import { URL } from '../../constants'
import { openLinkNewTab } from '../../utils/url'

type ReduxProps = ConnectedProps<typeof connector>

interface Props extends FormInterface, WizardInterface, IntlInterface, ReduxProps {
  isModal: boolean
  onClose: any
  onContinue: any
}

interface State {
  warnings: {
    firstName?: string
    lastName?: string
  }
}

const FORM_NAME = 'ClientInfo'

const goToIb = () => {
  openLinkNewTab(URL.INTERNET_BANK)
}
export class ClientInfo extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    props.initialize({ ...props.storeData })
  }

  componentDidMount() {
    const notificationRet = this.props.showNotification({
      content: (
        <React.Fragment>
          {this.props.translate(messages.clientInfo.notification.text)}&nbsp;
          <BLink
            type="link"
            onClick={goToIb}
            message={this.props.translate(messages.clientInfo.notification.link)}
          />
        </React.Fragment>
      ),
      severity: NotificationSeverityEnum.Warning,
    })

    this.notificationId = get(notificationRet, 'id')
  }

  componentWillUnmount(): void {
    this.notificationId && this.props.hideNotification(this.notificationId)
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveClientInfoCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      window.location.href = this.props.referrerUrl
    }
  }

  notificationId: string = ''

  saveClientInfoCallback = () => {
    this.props.saveClientInfo({
      success: () => {
        this.props.logPhoneNumber(get(this.props, 'values.phoneNumber', ''))
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && (errorInfo.errors || errorInfo.warnings)) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
          if (errorInfo.warnings) {
            this.setState({ warnings: errorInfo.warnings })
            this.props.formChange(FORM_NAME, 'warningsConfirmed', true)
          }
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  /**
   * Prevents checkbox to be changed when clicked on the link for showing modal dialog.
   * @param modalConfig
   */
  showModalDialogWrapper = (modalConfig: Record<string, any>) => (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    this.props.showModalDialog(modalConfig)
  }

  // @ts-ignore
  handleNameChange = (_event, _value, _oldValue, field) => {
    if (field === 'firstName' || field === 'lastName') {
      // @ts-ignore
      if (this.state?.warnings[field] !== undefined) {
        this.setState(({ warnings }) => ({
          warnings: { ...warnings, [field]: undefined },
        }))
        this.props.formChange(FORM_NAME, 'warningsConfirmed', false)
      }
    }
  }

  render() {
    const { invalid, isModal, translate } = this.props

    return (
      <BForm id={FORM_NAME}>
        <Title
          center={isModal}
          message={messages.clientInfo.title}
          subTitle={!isModal}
          subTitleMessage={messages.clientInfo.subTitle}
        />
        <BRow>
          <BCol sm={12} xs={12} md={6} lg={6}>
            <BTextInput
              name="firstName"
              messages={{
                label: translate(messages.clientInfo.firstName),
                warning: this.state?.warnings?.firstName,
              }}
              testid="firstName"
              onChange={this.handleNameChange}
            />
          </BCol>
          <BCol sm={12} xs={12} md={6} lg={6}>
            <BTextInput
              name="lastName"
              messages={{
                label: translate(messages.clientInfo.lastName),
                warning: this.state?.warnings?.lastName,
              }}
              testid="lastName"
              onChange={this.handleNameChange}
            />
          </BCol>
        </BRow>
        <BRow>
          <BCol sm={12} xs={12} md={6} lg={6}>
            <BTextInput
              name="email"
              messages={{
                label: translate(messages.clientInfo.email),
              }}
              testid="email"
            />
          </BCol>
          <BCol sm={12} xs={12} md={6} lg={6}>
            <BPhoneInput
              name="phoneNumber"
              messages={{
                label: translate(messages.clientInfo.phoneNumber),
                notice: translate(messages.clientInfo.phoneNote),
              }}
              testid="phoneNumber"
            />
          </BCol>
        </BRow>
        <hr />
        <BCheckbox
          name="consentToOfferMarketing"
          message={
            <FormattedMessage
              {...messages.clientInfo.marketing.text}
              values={{
                link: (
                  <BLink onClick={this.showModalDialogWrapper(CLIENT_INFO_MARKETING)}>
                    {translate(messages.clientInfo.marketing.link)}
                  </BLink>
                ),
              }}
            />
          }
          testid="consentToOfferMarketing"
        />

        <BCheckbox
          name="consentCommUnsecured"
          message={
            <FormattedMessage
              {...messages.clientInfo.encryption.text}
              values={{
                link: (
                  <BLink onClick={this.showModalDialogWrapper(CLIENT_INFO_ENCRYPTION)}>
                    {translate(messages.clientInfo.encryption.link)}
                  </BLink>
                ),
              }}
            />
          }
          testid="consentCommUnsecured"
        />

        <BCheckbox
          name="consentInstantMessaging"
          message={
            <FormattedMessage
              {...messages.clientInfo.imConsent.text}
              values={{
                link: (
                  <BLink onClick={this.showModalDialogWrapper(CLIENT_INFO_IM)}>
                    {translate(messages.clientInfo.imConsent.link)}
                  </BLink>
                ),
              }}
            />
          }
          testid="consentInstantMessaging"
        />

        <BText>
          <FormattedMessage
            {...messages.clientInfo.consent.text}
            values={{
              dataProcessing: (
                <u>
                  <BLink
                    color="link"
                    href={URL.GDPR_MONETA}
                    message={messages.clientInfo.consent.dataProcessing}
                    target="_blank"
                  />
                </u>
              ),
            }}
          />
        </BText>

        <PageControlButtons
          onSubmit={this.onSubmit}
          disableSubmit={invalid}
          isModal={isModal}
          secondaryAction={this.onBack}
        />

        <PrefillFormButton formName="ClientInfo" />
      </BForm>
    )
  }
}

const connector = connect(
  (state: ReduxState) => ({
    referrerUrl: state.wizard.referrerUrl,
    storeData: {
      consentCommUnsecured: state.client.consentCommUnsecured,
      consentInstantMessaging: state.client.consentInstantMessaging,
      consentToOfferMarketing: state.client.consentToOfferMarketing,
      email: state.client.email,
      firstName: state.client.firstName,
      lastName: state.client.lastName,
      phoneNumber: state.client.phoneNumber,
      warningsConfirmed: false,
    },
  }),
  {
    hideNotification: notificationActions.hideNotification,
    logPhoneNumber: analyticsActions.logPhoneNumber,
    saveClientInfo: clientActions.saveClientInfo,
    showModalDialog: modalDialogActions.showModalDialog,
    formChange: change,
    showNotification: notificationActions.showNotification,
  }
)

export default compose(
  errorHandler('containers/ClientInfo/ClientInfo.tsx'),
  wizard,
  connector,
  form({
    name: FORM_NAME,
    validate,
  }),
  intl
)(ClientInfo)
